import {
  appendHSError,
  removeHSErrors,
  removeHSCustomErrors
} from '@/formshandler/custom-hs-validation';
import {sendDataLayerEvent, throwRollbarError} from 'utils/analytics';
import axios from 'axios';

// Check if email format is valid
const isEmailFormatValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

// BriteVerify documentation: https://docs.briteverify.com/
const EMAIL_NOT_VALID_ERROR = 'Please enter a valid email address.';

const VALIDATION_TIMEOUT = 2200;

// BriteVerify email status codes
const EMAIL_STATUS = {
  VALID: 'valid',
  INVALID: 'invalid',
  UNKNOWN: 'unknown',
  ACCEPT_ALL: 'accept_all'
};

const REQUEST_STATUS = {
  INVALID: 'invalid',
  VALID: 'valid',
  VALIDATING: 'validating'
};

export const validateEmailOnChange = formId => {
  const form = document.querySelector(`[data-form-id='${formId}']`);
  const emailInput = form.querySelector('input[name="email"]');
  const emailWrapper = form.querySelector('.hs_email');
  const emailValidityInput = form.querySelector('input[name="email_validity"]');
  const formSubmitButton = form.querySelector('input[type="submit"]');

  let status = REQUEST_STATUS.VALID; // This variable will store the status of the email validation.
  let lastValidatedEmail = ''; // This variable will store the last email that was validated.
  let verifyTimer = null; // This variable will store the setTimeout function to validate the email after the timeout.
  let submitIfValid = false; // This variable will submit the form once the email is validated and the status is valid.

  const setEmailValidated = () => {
    status = REQUEST_STATUS.VALID;
    removeHSCustomErrors(emailWrapper);
    if (submitIfValid) formSubmitButton.click();
  };

  const setEmailUnValidated = () => {
    status = REQUEST_STATUS.INVALID;
    removeHSCustomErrors(emailWrapper);
    appendHSError(emailWrapper, EMAIL_NOT_VALID_ERROR);
  };

  const validateEmail = () => {
    const email = emailInput.value;

    if (!isEmailFormatValid(email) || status === REQUEST_STATUS.VALIDATING) return;

    removeHSCustomErrors(emailWrapper);
    lastValidatedEmail = email;
    status = REQUEST_STATUS.VALIDATING;

    axios
      .post(
        `${process.env.NEXT_PUBLIC_BRITEVERIFY_API_ENDPOINT}fullverify`,
        {email},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `ApiKey: ${process.env.NEXT_PUBLIC_BRITEVERIFY_API_KEY}`
          }
        }
      )
      .then(response => {
        if (response.data?.email) {
          const {status: emailStatus} = response.data.email;
          if (emailValidityInput) emailValidityInput.value = emailStatus;
          sendDataLayerEvent('emailValidation', {emailStatus});
          removeHSCustomErrors(emailWrapper);

          if (emailStatus === EMAIL_STATUS.INVALID) return setEmailUnValidated();

          // for the rest of the status codes (Valid, Unknown and Accept All)
          return setEmailValidated();
        }

        // if BriteVerify API fails, we still want to allow form submission
        return setEmailValidated();
      })
      .catch(error => {
        throwRollbarError(error);

        // if BriteVerify API fails, we still want to allow form submission
        return setEmailValidated();
      })
      .finally(() => {
        submitIfValid = false;
        if (verifyTimer) {
          clearTimeout(verifyTimer);
        }
      });
  };

  // Attach event listener to form submit button to prevent form submission if email is invalid.
  formSubmitButton.addEventListener('click', event => {
    const email = emailInput.value;

    if (status === REQUEST_STATUS.VALIDATING) {
      submitIfValid = true;
      verifyTimer = setTimeout(() => setEmailValidated(), VALIDATION_TIMEOUT);
    }

    // If the email is invalid, prevent form submission.
    if (
      (status === REQUEST_STATUS.VALID && lastValidatedEmail !== email) ||
      status === REQUEST_STATUS.VALIDATING ||
      status === REQUEST_STATUS.INVALID
    ) {
      return event.preventDefault();
    }

    if (status === REQUEST_STATUS.INVALID) {
      appendHSError(emailWrapper, EMAIL_NOT_VALID_ERROR);
      removeHSErrors(emailWrapper);
    }
  });

  emailInput.addEventListener('change', event => validateEmail());
};
